import * as _ from 'lodash-es'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useReportStore } from '@/modules/report/stores/ReportStore'

import { ReportScheduleId, ReportScheduleWrite } from '@generated/types'

type TemporaryScheduleId = `new_schedule_${string}`
type EditableScheduleId = ReportScheduleId | TemporaryScheduleId
interface EditableReportSchedule extends Omit<ReportScheduleWrite, 'id'> {
  id: EditableScheduleId
}

export const useReportUpdateStore = defineStore('reportUpdateStore', () => {
  const reportStore = useReportStore()
  const { reportSchedules } = storeToRefs(reportStore)
  const updatedReportSchedules = ref<EditableReportSchedule[]>([])
  watch(
    reportSchedules,
    newSchedules => {
      updatedReportSchedules.value = _.cloneDeep(newSchedules)
    },
    { immediate: true, deep: true },
  )

  function addReportSchedule(): EditableReportSchedule {
    const temporaryId: TemporaryScheduleId = `new_schedule_${_.uniqueId()}`
    const newSchedule: EditableReportSchedule = {
      id: temporaryId,
      name: 'Weekly Report',
      report_type: 'sales',
      recipients: [],
      frequency: 'weekly',
      args: {},
    }
    updatedReportSchedules.value.push(newSchedule)
    return newSchedule
  }

  function updateReportSchedule(
    scheduleUpdates: Pick<EditableReportSchedule, 'id'> &
      Partial<Omit<EditableReportSchedule, 'id'>>,
  ): void {
    const index = updatedReportSchedules.value.findIndex(s => s.id === scheduleUpdates.id)
    if (index === -1) return
    const existingSchedule = updatedReportSchedules.value[index]
    updatedReportSchedules.value[index] = {
      ...existingSchedule,
      ..._.omit(scheduleUpdates, 'id'), // Ensure ID isn't overwritten by partial update
    }
  }

  const hasUpdates = computed<boolean>(() => {
    return !_.isEqual(reportSchedules.value, updatedReportSchedules.value)
  })
  function discardUpdates(): void {
    updatedReportSchedules.value = _.cloneDeep(reportSchedules.value)
  }
  const isSaving = ref<boolean>(false)
  async function saveUpdates(): Promise<void> {
    if (!hasUpdates.value) return

    try {
      isSaving.value = true
      const promises: Promise<void>[] = []
      for (const updatedSchedule of updatedReportSchedules.value) {
        // Create: Temporary string ID exists
        if (typeof updatedSchedule.id === 'string') {
          const scheduleData: ReportScheduleWrite = {
            name: updatedSchedule.name,
            report_type: updatedSchedule.report_type,
            recipients: updatedSchedule.recipients,
            frequency: updatedSchedule.frequency,
            args: updatedSchedule.args,
          }
          promises.push(reportStore.createSalesReport(scheduleData))
        }
        // Update: Real number ID exists
        else if (typeof updatedSchedule.id === 'number') {
          const originalSchedule = reportSchedules.value.find(s => s.id === updatedSchedule.id)
          // Check if it actually changed compared to the original from reportStore
          if (originalSchedule && !_.isEqual(originalSchedule, updatedSchedule)) {
            const scheduleData: ReportScheduleWrite & { id: ReportScheduleId } = {
              id: updatedSchedule.id,
              name: updatedSchedule.name,
              report_type: updatedSchedule.report_type,
              recipients: updatedSchedule.recipients,
              frequency: updatedSchedule.frequency,
              args: updatedSchedule.args,
            }
            promises.push(reportStore.updateSalesReport(scheduleData))
          }
        }
      }
      await Promise.all(promises)
      await reportStore.getSalesReport()
    } finally {
      isSaving.value = false
    }
  }

  return {
    updatedReportSchedules,
    hasUpdates,
    isSaving,
    addReportSchedule,
    updateReportSchedule,
    discardUpdates,
    saveUpdates,
  }
})
